const normalizeImages = (images) =>
  (Array.isArray(images) ? images : []).map((img) => ({
    id: img.id,
    name: img.name || "",
    mimeType: img.mimeType || "",
    path: img.path || "",
    url: img.url || "",
    signedAt: img.signedAt || "",
  }));

const normalizeAssets = (assets) =>
  (Array.isArray(assets) ? assets : []).map((asset) => ({
    id: asset.id,
    name: asset.name || "",
    mimeType: asset.mimeType || "",
    path: asset.path || "",
    url: asset.url || "",
    signedAt: asset.signedAt || "",
  }));

const compactText = (value, maxLength = 4000) => {
  const raw = String(value || "")
    .replace(/\s+/g, " ")
    .trim();
  if (!raw) return "";
  if (!maxLength || raw.length <= maxLength) return raw;
  return `${raw.slice(0, Math.max(0, maxLength - 3))}...`;
};

const normalizeDocs = (docs, maxTextLength = 4000) =>
  (Array.isArray(docs) ? docs : []).map((doc) => ({
    id: doc.id,
    name: doc.name || "",
    mimeType: doc.mimeType || "",
    size: doc.size || 0,
    path: doc.path || "",
    url: doc.url || "",
    signedAt: doc.signedAt || "",
    text: compactText(doc.text || "", maxTextLength),
  }));

const compactContext = (value, depth = 0) => {
  if (value == null) return value;
  if (typeof value === "string") return compactText(value, 220);
  if (typeof value === "number" || typeof value === "boolean") return value;
  if (Array.isArray(value)) {
    return value.slice(0, 6).map((item) => compactContext(item, depth + 1));
  }
  if (typeof value === "object") {
    const entries = Object.entries(value).slice(0, 20);
    return entries.reduce((acc, [key, val]) => {
      acc[key] = compactContext(val, depth + 1);
      return acc;
    }, {});
  }
  return value;
};

const stripEmpty = (value) => {
  if (value == null) return undefined;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed ? trimmed : undefined;
  }
  if (typeof value === "number" || typeof value === "boolean") return value;
  if (Array.isArray(value)) {
    const cleaned = value
      .map((item) => stripEmpty(item))
      .filter((item) => item !== undefined);
    return cleaned.length > 0 ? cleaned : undefined;
  }
  if (typeof value === "object") {
    const next = Object.entries(value).reduce((acc, [key, val]) => {
      const cleaned = stripEmpty(val);
      if (cleaned !== undefined) {
        acc[key] = cleaned;
      }
      return acc;
    }, {});
    return Object.keys(next).length > 0 ? next : undefined;
  }
  return value;
};

const uint8ToBase64 = (bytes) => {
  let binary = "";
  const chunkSize = 0x8000;
  for (let index = 0; index < bytes.length; index += chunkSize) {
    const chunk = bytes.subarray(index, index + chunkSize);
    binary += String.fromCharCode(...chunk);
  }
  return btoa(binary);
};

export const encodeKnowledgeBundleForTransport = async (bundle) => {
  if (
    typeof CompressionStream === "undefined" ||
    typeof TextEncoder === "undefined" ||
    typeof Response === "undefined" ||
    typeof btoa === "undefined"
  ) {
    return { data: bundle, dataEncoding: "" };
  }

  try {
    const json = JSON.stringify(bundle || {});
    const stream = new CompressionStream("gzip");
    const writer = stream.writable.getWriter();
    await writer.write(new TextEncoder().encode(json));
    await writer.close();
    const compressed = await new Response(stream.readable).arrayBuffer();
    return {
      data: uint8ToBase64(new Uint8Array(compressed)),
      dataEncoding: "gzip-base64-json",
    };
  } catch (error) {
    return { data: bundle, dataEncoding: "" };
  }
};

export const buildKnowledgeBundle = ({
  knowledgeDb,
  tags,
  postTypeTags,
  postExamples,
  postCreatives,
  brandLibrary,
  aiProfile,
  analysisKnowledge,
  adCopyKnowledge,
  blogKnowledge,
  blogSeo,
  history,
  adCopyCreatives,
  activityPreferences,
}) => {
  const historyItems = Array.isArray(history) ? history.slice(0, 20) : [];
  const adCopyItems = Array.isArray(adCopyCreatives)
    ? adCopyCreatives.slice(0, 40)
    : [];
  const postCreativeItems = Array.isArray(postCreatives)
    ? postCreatives.slice(0, 40)
    : [];
  const bundle = {
    version: 1,
    updatedAt: new Date().toISOString(),
    knowledgeDb: (Array.isArray(knowledgeDb) ? knowledgeDb : []).map((item) => ({
      id: item.id,
      name: item.name || "",
      notes: compactText(item.notes || "", 4000),
      createdAt: item.createdAt || new Date().toISOString(),
      images: normalizeImages(item.images),
    })),
    tags: Array.isArray(tags) ? tags : [],
    postTypeTags: (Array.isArray(postTypeTags) ? postTypeTags : []).map(
      (tag) => ({
        id: tag.id,
        name: tag.name || "",
        description: tag.description || "",
        extraInfo: tag.extraInfo || "",
        format: tag.format || "meta_ads",
        starred: Boolean(tag.starred),
        docs: normalizeAssets(tag.docs),
      }),
    ),
    postExamples: (Array.isArray(postExamples) ? postExamples : []).map(
      (folder) => ({
        ...folder,
        images: (folder?.images || []).map((img) => ({
          ...img,
          context: img?.context ? compactContext(img.context) : img?.context,
        })),
      }),
    ),
    brandLibrary: {
      name: brandLibrary?.name || "",
      message: compactText(brandLibrary?.message || "", 1200),
      colors: brandLibrary?.colors || [],
      fonts: brandLibrary?.fonts || [],
      assets: normalizeAssets(brandLibrary?.assets),
    },
    aiProfile: aiProfile || {
      instructions: "",
      personality: "",
      mustDo: "",
      avoid: "",
      referenceUrls: [],
    },
    analysisKnowledge: {
      notes: compactText(analysisKnowledge?.notes || "", 8000),
      docs: normalizeDocs(analysisKnowledge?.docs, 8000),
    },
    adCopyKnowledge: {
      notes: compactText(adCopyKnowledge?.notes || "", 2000),
      avoid: compactText(adCopyKnowledge?.avoid || "", 1600),
      docs: normalizeDocs(adCopyKnowledge?.docs, 3500),
      headlines: Array.isArray(adCopyKnowledge?.headlines)
        ? adCopyKnowledge.headlines
        : [],
      primaryTexts: Array.isArray(adCopyKnowledge?.primaryTexts)
        ? adCopyKnowledge.primaryTexts
        : [],
    },
    blogKnowledge: {
      notes: compactText(blogKnowledge?.notes || "", 2000),
      goals: compactText(blogKnowledge?.goals || "", 1200),
      audience: compactText(blogKnowledge?.audience || "", 1200),
      mustInclude: compactText(blogKnowledge?.mustInclude || "", 1200),
      avoid: compactText(blogKnowledge?.avoid || "", 1200),
      cta: compactText(blogKnowledge?.cta || "", 800),
      docs: normalizeDocs(blogKnowledge?.docs, 3500),
    },
    blogSeo: {
      metaTitle: compactText(blogSeo?.metaTitle || "", 200),
      metaDescription: compactText(blogSeo?.metaDescription || "", 400),
      keywords: Array.isArray(blogSeo?.keywords) ? blogSeo.keywords : [],
      slug: compactText(blogSeo?.slug || "", 160),
      canonicalUrl: compactText(blogSeo?.canonicalUrl || "", 400),
      schemaMarkup: compactText(blogSeo?.schemaMarkup || "", 4000),
    },
    history: historyItems,
    adCopyCreatives: adCopyItems,
    postCreatives: postCreativeItems,
    activityPreferences: Array.isArray(activityPreferences)
      ? activityPreferences
      : [],
  };
  const pruned = stripEmpty(bundle);
  return pruned || { version: bundle.version, updatedAt: bundle.updatedAt };
};

export const normalizeKnowledgeBundle = (bundle) => {
  const data = bundle?.data || bundle || {};
  return {
    knowledgeDb: Array.isArray(data.knowledgeDb) ? data.knowledgeDb : [],
    tags: Array.isArray(data.tags) ? data.tags : [],
    postTypeTags: (Array.isArray(data.postTypeTags) ? data.postTypeTags : []).map(
      (tag) => ({
        id: tag.id,
        name: tag.name || "",
        description: tag.description || "",
        extraInfo: tag.extraInfo || "",
        format: tag.format || "meta_ads",
        starred: Boolean(tag.starred),
        docs: normalizeAssets(tag.docs),
      }),
    ),
    postExamples: Array.isArray(data.postExamples) ? data.postExamples : [],
    postCreatives: Array.isArray(data.postCreatives) ? data.postCreatives : [],
    brandLibrary: data.brandLibrary || {
      name: "",
      message: "",
      colors: [],
      fonts: [],
      assets: [],
    },
    aiProfile: data.aiProfile || {
      instructions: "",
      personality: "",
      mustDo: "",
      avoid: "",
      referenceUrls: [],
    },
    analysisKnowledge: data.analysisKnowledge || { notes: "", docs: [] },
    adCopyKnowledge: {
      notes: data?.adCopyKnowledge?.notes || "",
      avoid: data?.adCopyKnowledge?.avoid || "",
      docs: Array.isArray(data?.adCopyKnowledge?.docs)
        ? data.adCopyKnowledge.docs
        : [],
      headlines: Array.isArray(data?.adCopyKnowledge?.headlines)
        ? data.adCopyKnowledge.headlines
        : [],
      primaryTexts: Array.isArray(data?.adCopyKnowledge?.primaryTexts)
        ? data.adCopyKnowledge.primaryTexts
        : [],
    },
    blogKnowledge: data.blogKnowledge || {
      notes: "",
      goals: "",
      audience: "",
      mustInclude: "",
      avoid: "",
      cta: "",
      docs: [],
    },
    blogSeo: data.blogSeo || {
      metaTitle: "",
      metaDescription: "",
      keywords: [],
      slug: "",
      canonicalUrl: "",
      schemaMarkup: "",
    },
    history: Array.isArray(data.history) ? data.history : [],
    adCopyCreatives: Array.isArray(data.adCopyCreatives)
      ? data.adCopyCreatives
      : [],
    activityPreferences: Array.isArray(data.activityPreferences)
      ? data.activityPreferences
      : [],
  };
};
